import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Box from '@mui/material/Box';
import Icon from 'common/components/Elements/Icon/Icon';
import { arraySum } from 'common/utilities/core/array';
import type * as I from 'core/api/coreApiClient';
import Command from 'core/components/Elements/Command/Command';
import { replaceCommand } from 'core/redux/actions/command/commandActions';
import type { ReplacedCommands } from 'core/redux/reducers/page/pageReducer.types';
import type { ApplicationState } from 'core/redux/reducers/rootReducer';
import type { AppDispatch } from 'core/redux/store/configureStore.types';
import { groupCommands, replaceCommands } from 'core/utilities/command/command';
import type { EnhancedCommand } from 'core/utilities/core.types';
import { sx } from 'core/utilities/theme';
import debounce from 'debounce';
import React, { useCallback, useRef } from 'react';
import isEqual from 'react-fast-compare';
import { useDispatch, useSelector } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';

import styles from './Commands.style';

interface ComponentProps {
  id: string;
  commands: EnhancedCommand[];
  /** Group commands within more from the left or right (default left) */
  groupFrom?: 'left' | 'right';
  /** Never group commands (no responsiveness) */
  disableResponsive?: boolean;
  onProcessUpdatedCommands?: (commands: EnhancedCommand[]) => EnhancedCommand[];
  /** Skip hover on root level commands */
  hoverColor?: string;
}

const emptyReplacedCommands: ReplacedCommands = {};

/**
 * Display commands one next to another.
 *
 * 1. Some async commands might be replaced once loaded (commands = props commands + replacedCommands)
 * 2. Render formattedCommands and cache each command width on the screen
 * 3. Based on the width available, show max amount of commands, remaining will be nested under
 *    a newly create "more" command
 * 4. On resize or on command update (async):
 *    recalculate commands to show inline or under "more" (use cached width values)
 *
 * Note: by default we hide commands from the left (so we calculate the width commands are taking from the right)
 *       use the groupFrom param to do the opposite (used in App Header)
 */
function Commands({
  id, commands: propsCommands, groupFrom, disableResponsive, onProcessUpdatedCommands, hoverColor,
}: ComponentProps) {
  const classes = styles({ hoverColor });

  const ref = React.createRef<HTMLDivElement>();
  const dispatch = useDispatch<AppDispatch>();
  const replacedCommands: ReplacedCommands = useSelector(
    (state: ApplicationState) => state.page.groupsOfReplacedCommands[id] || emptyReplacedCommands,
  );
  const [commands, setCommands] = React.useState<EnhancedCommand[]>([]);
  /** From which command number should we move them under "more" */
  const [moreBreakpoint, setMoreBreakpointState] = React.useState<number | undefined>();
  /** Commands' widths is only calculated once during the first render (all are rendered) */
  const [commandsWidths, setCommandsWidth] = React.useState<number[] | undefined>();
  const [resizeId, setResizeId] = React.useState<number>(0);

  const handleReplaceCommand = (command: EnhancedCommand, newCommands: EnhancedCommand[]) => {
    dispatch(replaceCommand(id, command, newCommands));
  };

  const cachedFormatCommandsUsingMoreBreakpoint = useRef<I.Command[] | undefined>();

  const setMoreBreakpoint = useCallback((newMoreBreakpoint: typeof moreBreakpoint) => {
    cachedFormatCommandsUsingMoreBreakpoint.current = undefined;
    setMoreBreakpointState(newMoreBreakpoint);
  }, []);

  /**
   * Final list of commands been displayed based on moreBreakpoint.
   * Commands will be aggregated under a "more" dropdown command if they can't all fit on the screen.
   */
  const formatCommandsUsingMoreBreakpoint = () => {
    if (cachedFormatCommandsUsingMoreBreakpoint.current) {
      return cachedFormatCommandsUsingMoreBreakpoint.current;
    }

    let moreCommand: I.Command | undefined;
    let inlineCommands: I.Command[] = commands;

    if (moreBreakpoint) {
      if (groupFrom === 'right') {
        moreCommand = groupCommands(
          'More',
          <Box mt="-2px" ml={-0.5} maxHeight="16px"><Icon element={MoreHorizIcon} /></Box>,
          commands.slice(moreBreakpoint),
        );
        inlineCommands = commands.slice(0, moreBreakpoint);
      } else {
        moreCommand = groupCommands(
          'More',
          <Box mt="-2px" ml={-0.5} maxHeight="16px"><Icon element={MoreHorizIcon} /></Box>,
          commands.slice(0, commands.length - moreBreakpoint),
        );
        inlineCommands = commands.slice(commands.length - moreBreakpoint);
      }
    }

    const allCommands: I.Command[] = [];
    allCommands.push(...inlineCommands);
    if (allCommands.length > 0 && moreCommand && moreCommand.commands.length > 0) {
      allCommands.push(moreCommand);
    }

    cachedFormatCommandsUsingMoreBreakpoint.current = allCommands;
    return allCommands;
  };

  const calcBreakpoint = useCallback(() => {
    if (!ref?.current || !commandsWidths) return;

    const commandsWidth = arraySum(commandsWidths);

    const containerWidth = (ref.current.parentNode as HTMLElement).offsetWidth;

    // Add one pixel to the container as the calculation might be off with some browser / zoom level combination
    // Example: Chrome + zoom level 125%
    if (commandsWidth > containerWidth + 1) {
      const containerWidthWithoutMoreButtonWidth = containerWidth - 32; // remove the "more" width
      let width = 0;
      let breakpoint: number | undefined;

      commandsWidths.forEach((nodeWidth, i) => {
        if (width + nodeWidth < containerWidthWithoutMoreButtonWidth) {
          width += nodeWidth;
        } else if (!breakpoint) {
          breakpoint = i;
        }
      });

      if (breakpoint) {
        setMoreBreakpoint(breakpoint);
      }
    }
  }, [commandsWidths, ref, setMoreBreakpoint]);

  const delayedCalcBreakpoint = useDebouncedCallback(() => {
    calcBreakpoint();
  }, 10);

  React.useEffect(() => {
    let finalCommands = replaceCommands(propsCommands, replacedCommands);

    if (onProcessUpdatedCommands) {
      finalCommands = onProcessUpdatedCommands(finalCommands);
    }

    // Ignore functions when comparing
    if (!isEqual(
      commands.map((c) => ({ ...c, onClick: undefined })),
      finalCommands.map((c) => ({ ...c, onClick: undefined })),
    )) {
      // Ensure we recalculate the the commands width if we get an updated set
      setCommandsWidth(undefined);
      setMoreBreakpoint(undefined);
    }

    setCommands(finalCommands);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propsCommands, replacedCommands]);

  React.useEffect(() => {
    if (disableResponsive) return;
    delayedCalcBreakpoint();
  }, [id, disableResponsive, commandsWidths, resizeId, delayedCalcBreakpoint]);

  /**
   * Re-calculate which command need to go under the "more" dropdown
   * when the user resize the screen
   */
  const handleResize = () => {
    setMoreBreakpoint(undefined);
    setResizeId((prevState) => prevState + 1);
  };

  const debouncedHandleResize = debounce(() => handleResize(), 500);

  React.useEffect(() => {
    window.addEventListener('resize', debouncedHandleResize);
    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, [debouncedHandleResize]);

  // Recalculate commands widths whenever it is reset, usually due to async commands been loaded
  React.useEffect(() => {
    if (ref && ref.current && !commandsWidths) {
      const newCommandsWidths: number[] = [];

      let nodes = Array.from(ref.current.childNodes[0].childNodes) as HTMLElement[];
      if (groupFrom !== 'right') nodes = nodes.reverse();

      nodes.forEach((node) => {
        const nodeWidth = node.offsetWidth
          + Number(window.getComputedStyle(node as Element).marginLeft.replace('px', ''));
        newCommandsWidths.push(nodeWidth);
      });

      setCommandsWidth(newCommandsWidths);
    }
  }, [ref, commandsWidths, groupFrom, id]);

  const formattedCommands = formatCommandsUsingMoreBreakpoint();

  if (formattedCommands.length === 0) return null;

  return (
    <Box sx={{ display: 'inline-block', whiteSpace: 'nowrap' }} ref={ref}>
      <Box
        display="inline-block"
        sx={sx(
          classes.commands,
          hoverColor ? classes.commandsHover : undefined,
        )}
        data-e2e-commands={id}
        data-testid="Commands"
      >
        {formattedCommands.map((command, i) => (
          <Command
            key={i.toString() + (command.id || 'noId')}
            command={command}
            replaceCommand={handleReplaceCommand}
          />
        ))}
      </Box>
    </Box>
  );
}

export default Commands;
